export const EXTEND_VIDEO_MIN_EXTENSION_SECONDS = 5;
export const EXTEND_VIDEO_MAX_EXTENSION_SECONDS = 15;

// Source-video limits are independent of the generated extension durations configured below.
export const MINIMAX_H3_MAX_SOURCE_DURATION_SECONDS = 60;
export const FLUX_3_MAX_SOURCE_DURATION_SECONDS = 15;
export const VEO_3_1_MAX_SOURCE_DURATION_SECONDS = 8;

const MINIMAX_H3_MAX_EXTEND_VIDEO_MODEL_ID = 'minimax/h3-max/extend-video';
const FLUX_3_EXTEND_VIDEO_MODEL_ID = 'blackforestlabs/flux-3/extend-video';
const VEO_3_1_EXTEND_VIDEO_MODEL_ID = 'fal-ai/veo3.1/extend-video';
const FLUX_3_MIN_EXTENSION_SECONDS = 5;
const FLUX_3_MAX_EXTENSION_SECONDS = 20;
const FLUX_3_EXTENSION_STEP_SECONDS = 5;
const VEO_3_1_EXTENSION_SECONDS = 7;

export type ExtendVideoDurationConfig = {
  defaultSeconds: number;
  isFixed: boolean;
  maxSeconds: number;
  minSeconds: number;
  parameterFormat: 'number' | 'secondsString';
  step: number;
  validationMessage: string;
  validSeconds: readonly number[];
};

export type ExtendVideoDuration = {
  parameter: number | string;
  seconds: number;
};

export type ExtendVideoSourceDurationConstraint = {
  boundary: 'exclusive' | 'inclusive';
  limitSeconds: number;
  validationMessage: string;
};

const MINIMAX_H3_MAX_DURATION_CONFIG: ExtendVideoDurationConfig = {
  defaultSeconds: EXTEND_VIDEO_MIN_EXTENSION_SECONDS,
  isFixed: false,
  maxSeconds: EXTEND_VIDEO_MAX_EXTENSION_SECONDS,
  minSeconds: EXTEND_VIDEO_MIN_EXTENSION_SECONDS,
  parameterFormat: 'number',
  step: 1,
  validationMessage: `Enter a whole number from ${EXTEND_VIDEO_MIN_EXTENSION_SECONDS} to ${EXTEND_VIDEO_MAX_EXTENSION_SECONDS}.`,
  validSeconds: Array.from(
    { length: EXTEND_VIDEO_MAX_EXTENSION_SECONDS - EXTEND_VIDEO_MIN_EXTENSION_SECONDS + 1 },
    (_, index) => EXTEND_VIDEO_MIN_EXTENSION_SECONDS + index,
  ),
};

const FLUX_3_DURATION_CONFIG: ExtendVideoDurationConfig = {
  defaultSeconds: FLUX_3_MIN_EXTENSION_SECONDS,
  isFixed: false,
  maxSeconds: FLUX_3_MAX_EXTENSION_SECONDS,
  minSeconds: FLUX_3_MIN_EXTENSION_SECONDS,
  parameterFormat: 'number',
  step: FLUX_3_EXTENSION_STEP_SECONDS,
  validationMessage: 'Enter 5, 10, 15, or 20 seconds.',
  validSeconds: Array.from(
    { length: (FLUX_3_MAX_EXTENSION_SECONDS - FLUX_3_MIN_EXTENSION_SECONDS) / FLUX_3_EXTENSION_STEP_SECONDS + 1 },
    (_, index) => FLUX_3_MIN_EXTENSION_SECONDS + index * FLUX_3_EXTENSION_STEP_SECONDS,
  ),
};

const VEO_3_1_DURATION_CONFIG: ExtendVideoDurationConfig = {
  defaultSeconds: VEO_3_1_EXTENSION_SECONDS,
  isFixed: true,
  maxSeconds: VEO_3_1_EXTENSION_SECONDS,
  minSeconds: VEO_3_1_EXTENSION_SECONDS,
  parameterFormat: 'secondsString',
  step: 1,
  validationMessage: 'Veo 3.1 extends videos by 7 seconds.',
  validSeconds: [VEO_3_1_EXTENSION_SECONDS],
};

export const getExtendVideoDurationConfig = (modelId: string | undefined): ExtendVideoDurationConfig => {
  switch (modelId) {
    case FLUX_3_EXTEND_VIDEO_MODEL_ID:
      return FLUX_3_DURATION_CONFIG;
    case VEO_3_1_EXTEND_VIDEO_MODEL_ID:
      return VEO_3_1_DURATION_CONFIG;
    default:
      // Auto uses the backend default, currently MiniMax H3 Max. Unknown future models retain
      // that default until their duration schema is deliberately added here.
      return MINIMAX_H3_MAX_DURATION_CONFIG;
  }
};

export const getExtendVideoSourceDurationConstraint = (
  modelId: string | undefined,
): ExtendVideoSourceDurationConstraint | undefined => {
  switch (modelId) {
    case FLUX_3_EXTEND_VIDEO_MODEL_ID:
      return {
        boundary: 'exclusive',
        limitSeconds: FLUX_3_MAX_SOURCE_DURATION_SECONDS,
        validationMessage: `This video is too long for Flux 3. Choose a video shorter than ${FLUX_3_MAX_SOURCE_DURATION_SECONDS} seconds.`,
      };
    case VEO_3_1_EXTEND_VIDEO_MODEL_ID:
      return {
        boundary: 'inclusive',
        limitSeconds: VEO_3_1_MAX_SOURCE_DURATION_SECONDS,
        validationMessage: `This video is too long for Veo 3.1. Choose a video that is ${VEO_3_1_MAX_SOURCE_DURATION_SECONDS} seconds or shorter.`,
      };
    case undefined:
    case MINIMAX_H3_MAX_EXTEND_VIDEO_MODEL_ID:
      return {
        boundary: 'inclusive',
        limitSeconds: MINIMAX_H3_MAX_SOURCE_DURATION_SECONDS,
        validationMessage: `This video is too long to extend. Choose a video that is ${MINIMAX_H3_MAX_SOURCE_DURATION_SECONDS} seconds or shorter.`,
      };
    default:
      // Unknown future models remain backend-authoritative until their source constraints are added here.
      return undefined;
  }
};

export const isExtendVideoSourceDurationSupported = ({
  constraint,
  sourceDurationSeconds,
}: {
  constraint: ExtendVideoSourceDurationConstraint;
  sourceDurationSeconds: number;
}) =>
  constraint.boundary === 'exclusive'
    ? sourceDurationSeconds < constraint.limitSeconds
    : sourceDurationSeconds <= constraint.limitSeconds;

export const createExtendVideoDuration = ({
  config,
  seconds,
}: {
  config: ExtendVideoDurationConfig;
  seconds: number;
}): ExtendVideoDuration => ({
  parameter: config.parameterFormat === 'secondsString' ? `${seconds}s` : seconds,
  seconds,
});

const EXTEND_VIDEO_MODEL_IDS = [
  MINIMAX_H3_MAX_EXTEND_VIDEO_MODEL_ID,
  FLUX_3_EXTEND_VIDEO_MODEL_ID,
  VEO_3_1_EXTEND_VIDEO_MODEL_ID,
] as const;

/**
 * The model picker can pick any extend model, so a source is extendable when at least one model
 * accepts it. Undefined duration is not a block: some video nodes never store it.
 */
export const getExtendVideoSourceDurationDisabledReason = (
  sourceDurationSeconds: number | undefined,
): string | undefined => {
  if (sourceDurationSeconds === undefined) {
    return undefined;
  }

  const constraints = EXTEND_VIDEO_MODEL_IDS.flatMap(
    (modelId) => getExtendVideoSourceDurationConstraint(modelId) ?? [],
  );
  const isSupportedByAnyModel = constraints.some((constraint) =>
    isExtendVideoSourceDurationSupported({ constraint, sourceDurationSeconds }),
  );

  if (isSupportedByAnyModel) {
    return undefined;
  }

  const [mostPermissive] = [...constraints].sort((a, b) => b.limitSeconds - a.limitSeconds);

  return mostPermissive?.validationMessage;
};
